import './index.css'

import clsx from 'clsx'
import startCase from 'lodash/startCase'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'

import { CB_59_GROWTH_AT_MANAGE_SUBSCRIPTION } from '@disco/constants/FeatureTypes'
import { getSubscriptionCurrentAmount } from '@disco/state-management/selectors/subscription'
import type { UsersLimits } from '@disco/state-management/selectors/user'
import {
  getConstanceSettings,
  getUser,
} from '@disco/state-management/selectors/user'
import dayjs from '@disco/utils/dayjs'
import { pricingPlans } from '@disco/utils/pricing'
import { PLUS_ID, PRO_ID } from '@disco/utils/featuresAccessLevel'
import { plural } from '@disco/utils/string-utils'
import {
  formatDate,
  formatRelativeDate,
  formatTimestamp,
} from '@disco/utils/time-utils'
import { getUrlParams } from '@disco/utils/url'

import type { SubscriptionData } from 'types/subscription'
import type { AccountPlan } from 'types/business2020'

import CardNumber from 'components/common/card-number'
import { TracksMeter } from 'components/common/tracks-meter'
import { Box, Callout, Flex } from 'components/ds'
import ActionRow from 'components/ui/action-row'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import Card from 'components/ui/card'
import ContactUsLink from 'components/ui/contact-us-link'
import Heading from 'components/ui/heading'
import Icon from 'components/ui/icon'
import List from 'components/ui/list'
import ListItemWithIcon from 'components/ui/list-item-with-icon'
import Lozenge from 'components/ui/lozenge'
import RouteLink from 'components/ui/route-link'
import Spinner from 'components/ui/spinner'
import StaffOnly from 'components/ui/staff-only'
import Tooltip from 'components/ui/tooltip'

import useChargebeePricingPage from 'hooks/use-chargebee-pricing-page'
import { useSelector } from 'hooks/use-redux'

import DiscoverySuiteAddOns from './add-ons/DiscoverySuiteContainer'
import WatermarkingAddOns from './add-ons/WatermarkingContainer'
import BillingCallout from './billing-callout'
import BillingContactInformation from './billing-contact-information'
import SubscriptionAdmin from './SubscriptionAdmin'
import { SubscribedUserBillingCancelButton } from './SubscribedUserBillingCancelButton'
import ChargebeeScriptLoader from 'components/chargebee-form/chargebee-loader'
import captureError from '@disco/utils/captureError'

type Props = {
  planName: string
  usersLimits: UsersLimits
  tracksLimits: UsersLimits
  disabledUpdatePaymentDetails: boolean
  showUpdatePaymentDetails: boolean
  subscription: SubscriptionData
  paymentOverdue: boolean
  bankDraftSubscriber: boolean
  period: 'annual' | 'monthly' | undefined
  lastUserAddedDate: string
  noComponentPurchaseDueTo: '' | string
  // empty string = allowed
  enableChangeSeatsModal: boolean
  enableChangeTracksModal: boolean
  loading: boolean
  billingProviderComponentsLoading: boolean
  isArtist: boolean
  isAdmin: boolean
}
const SEATS_MESSAGE = 'I’d like to change the seats on my account'
const TRACKS_MESSAGE = 'I’d like to change the number of tracks on my account'

/**
 * SubscribedUserBilling component for displaying subscription and billing information
 *
 * A comprehensive billing interface that shows subscription details, plan features,
 * usage quotas, payment information, and add-on management. Includes functionality
 * for plan changes, subscription cancellation, and billing contact management.
 *
 * @param props - Component props
 * @param props.planName - Name of the current subscription plan
 * @param props.usersLimits - User quota and usage information
 * @param props.tracksLimits - Track quota and usage information
 * @param props.disabledUpdatePaymentDetails - Whether payment updates are disabled
 * @param props.showUpdatePaymentDetails - Whether to show payment update options
 * @param props.subscription - Current subscription data
 * @param props.paymentOverdue - Whether payment is overdue
 * @param props.bankDraftSubscriber - Whether user pays via bank draft
 * @param props.period - Billing period (annual/monthly)
 * @param props.noComponentPurchaseDueTo - Reason why component purchases are disabled
 * @param props.enableChangeSeatsModal - Whether seat changes are enabled
 * @param props.enableChangeTracksModal - Whether track changes are enabled
 * @param props.loading - Whether billing data is loading
 * @param props.billingProviderComponentsLoading - Whether billing provider components are loading
 * @param props.isArtist - Whether user is on artist plan
 * @param props.isAdmin - Whether user has admin permissions
 * @returns JSX element containing the complete billing interface
 */
const SubscribedUserBilling = (
  props: Props,
): React.ReactElement<'div'> | React.ReactNode => {
  const constance = useSelector(getConstanceSettings)
  const {
    planName,
    usersLimits,
    tracksLimits,
    disabledUpdatePaymentDetails,
    showUpdatePaymentDetails,
    subscription, // TODO: don't rely on `subscription` passed via both props/context - https://disco-team.atlassian.net/browse/SUB-517
    paymentOverdue,
    bankDraftSubscriber,
    period,
    lastUserAddedDate,
    noComponentPurchaseDueTo,
    enableChangeSeatsModal,
    enableChangeTracksModal,
    loading,
    billingProviderComponentsLoading,
    isArtist,
    isAdmin,
  } = props

  const user = useSelector(getUser)
  const isPlanOnContract = user?.business?.private_metadata?.plan_on_contract

  const [cancelSubscriptionModalOpen, setCancelSubscriptionModalOpen] =
    useState(false)

  const subscriptionData = subscription.chargebee
  const subscriptionAmount = useSelector(getSubscriptionCurrentAmount)

  const [hasChargebeeScriptLoaded, setHasChargebeeScriptLoaded] =
    useState(false)

  const { url: growthLink } = useChargebeePricingPage(
    '/settings/billing',
    Boolean(constance[CB_59_GROWTH_AT_MANAGE_SUBSCRIPTION]),
  )

  if (loading && !subscriptionData) return <Spinner />

  if (subscription?.error?.body?.error === 'invalid_subscription_id') {
    return (
      <div className="SubscribedUserBilling">
        <div className="SubscribedUserBilling_TitleWrapper">
          <Heading size={700}>Plan details</Heading>
        </div>
        <Alert status="danger" icon="warning">
          <p className="SubscribedUserBilling_AlertDescription">
            Sorry, we couldn’t find a subscription for this account.{' '}
            <ContactUsLink
              className="SubscribedUserBilling_ContactUsLink"
              intercomMsg="My billing page is showing 'subscription not found'."
            >
              Please contact support.
            </ContactUsLink>
          </p>
        </Alert>
      </div>
    )
  }

  let cardEnding = ''

  if (!subscription.loading && subscriptionData?.last_segment_of_card_number) {
    cardEnding = subscriptionData?.last_segment_of_card_number
  }

  let switchTo: 'monthly' | 'annual' | null = null

  if (!subscription.loading && subscriptionData?.expires_at) {
    if (period === 'annual') {
      switchTo = 'monthly'
    } else {
      switchTo = 'annual'
    }
  }

  let showBillingDetailsTitle = false

  const hasSubscription =
    !!subscriptionData?.subscription_id ||
    !!user?.business?.chargebee_subscription_id

  if (
    (!disabledUpdatePaymentDetails && hasSubscription) ||
    (cardEnding && !bankDraftSubscriber)
  ) {
    showBillingDetailsTitle = true
  }

  const updateBillingInfoButton = showUpdatePaymentDetails && (
    <div className="SubscribedUserBilling_Tooltip">
      <Tooltip
        content={
          disabledUpdatePaymentDetails
            ? 'Only admins on your account can make changes to your plan.'
            : undefined
        }
      >
        {disabledUpdatePaymentDetails ? (
          <span className="SubscribedUserBilling_ContactUsLink SubscribedUserBilling_ContactUsLink--Disabled">
            Update Billing info
          </span>
        ) : (
          <Link
            to="/settings/billing/update"
            className="SubscribedUserBilling_ContactUsLink"
          >
            Update Billing info
          </Link>
        )}
      </Tooltip>
    </div>
  )
  const tracksCount =
    tracksLimits.maxAsDisplayString === 'unlimited'
      ? startCase(tracksLimits.maxAsDisplayString)
      : tracksLimits.maxAsDisplayString
  const usersCount =
    usersLimits.maxAsDisplayString === 'unlimited'
      ? startCase(usersLimits.maxAsDisplayString)
      : usersLimits.maxAsDisplayString

  const WrapperUpdatePlanLink = text => {
    return (
      <Tooltip
        content={
          disabledUpdatePaymentDetails
            ? 'Only admins on your account can make changes to your plan.'
            : undefined
        }
      >
        {text}
      </Tooltip>
    )
  }

  const UpdateSeatsTracksLink = ({ text, enabled, intercomMsg }) => {
    if (billingProviderComponentsLoading && !disabledUpdatePaymentDetails) {
      return (
        <div
          className={clsx('SubscribedUserBilling_ContactUsLink', {
            'SubscribedUserBilling_ContactUsLink--Disabled':
              billingProviderComponentsLoading,
          })}
        >
          <Tooltip content="Loading...">{text}</Tooltip>
        </div>
      )
    }

    return enabled ? (
      disabledUpdatePaymentDetails ? (
        <span className="SubscribedUserBilling_ContactUsLink SubscribedUserBilling_ContactUsLink--Disabled">
          {WrapperUpdatePlanLink(text)}
        </span>
      ) : (
        <Link
          className="SubscribedUserBilling_ContactUsLink"
          to="/settings/billing/update-seats-and-tracks"
        >
          {WrapperUpdatePlanLink(text)}
        </Link>
      )
    ) : (
      <ContactUsLink
        className={clsx('SubscribedUserBilling_ContactUsLink', {
          'SubscribedUserBilling_ContactUsLink--Disabled':
            disabledUpdatePaymentDetails,
        })}
        intercomMsg={intercomMsg}
        disabled={disabledUpdatePaymentDetails}
      >
        {WrapperUpdatePlanLink(text)}
      </ContactUsLink>
    )
  }

  const activePlan: AccountPlan | undefined = pricingPlans.find(
    plan => plan.name === planName,
  )
  const planColour = activePlan?.chosenColor.line.backgroundColor
  const topFeatures = activePlan?.topFeatures || []
  const urlParams = getUrlParams(window.location.search)

  let billingRenewsDate = ''
  let billingRenewsBanner: React.ReactNode = null
  if (subscriptionData?.expires_at) {
    const renewalDate = dayjs(subscriptionData?.expires_at)
    const startDate = renewalDate.subtract(7, 'day')
    const endDate = renewalDate.add(3, 'day')
    // @ts-ignore error TS2339: Property 'isBetween' does not exist on type 'Dayjs'.
    const isWithinRange = dayjs().isBetween(startDate, endDate)
    billingRenewsDate = renewalDate.format(
      'MMMM D' + (period === 'annual' ? ', YYYY' : ''),
    )
    if (
      isWithinRange &&
      activePlan?.handle &&
      [PLUS_ID, PRO_ID].includes(activePlan.handle)
    ) {
      billingRenewsBanner = (
        <Callout.Root>
          <Callout.Text>
            <strong>DISCO Plus and Pro plan pricing has changed.</strong> The
            updated price takes effect on your first renewal after February 27,
            2026.{' '}
            <a
              href="https://support.disco.ac/home/plus-pro-plan-pricing-update-faq-2026"
              target="_blank"
              rel="noreferrer"
            >
              Learn more
            </a>
          </Callout.Text>
        </Callout.Root>
      )
    }
  }

  const showUpgradeCallout = () => {
    const { upgrade } = urlParams
    if (!upgrade) return null
    switch (upgrade) {
      case 'lyric-transcription':
        return (
          <BillingCallout
            icon="info-outline"
            size="1"
            color="blue"
            text="Lyric Transcription is available to customers on the Pro plan
                and above, with Discovery Suite. To access this feature, upgrade
                below."
          />
        )
      default:
        return null
    }
  }

  const pricingPlanList = (
    <List className="PricingPlan_ListFeatures ">
      {topFeatures.map((item, index) => (
        <List.Item key={index} className={'PricingPlan_ItemFeature'}>
          {item}
        </List.Item>
      ))}
    </List>
  )
  // Set or remove the localStorage flag for QA purposes
  // localStorage.setItem('show_plan_change_button', 'true')
  // localStorage.removeItem('show_plan_change_button')
  //TODO: remove this showChangePlanButtonRollout, the conditional check, and ContactUsLink once we're live
  // 1- Temp boolean to show the change plan button for rollout
  const showChangePlanButtonRollout =
    constance.PLAN_DOWNGRADES_ROLLOUT ||
    Boolean(localStorage.getItem('show_plan_change_button'))
  // 2- Perm boolean to disable the change plan button
  let noPlanChangeDueTo = ''

  // if (!selfServePlanChange) noPlanChangeDueTo += ' SELF_SERVE_PLAN_CHANGE'
  // TODO: remove isArtist check - replace with SELF_SERVE_PLAN_CHANGE
  if (isArtist) noPlanChangeDueTo += ' artist_plan'
  // TODO: remove planName check - replace with SELF_SERVE_PLAN_CHANGE
  if ('Enterprise' === planName) noPlanChangeDueTo += ' enterprise_plan'

  if (bankDraftSubscriber) noPlanChangeDueTo += ' bank_draft'
  if (isPlanOnContract) noPlanChangeDueTo += ' plan_on_contract'
  if (!isAdmin) noPlanChangeDueTo += ' not_admin'
  if (!period) noPlanChangeDueTo += ' unsupported_period' // TODO: enable billing on 2-year subscriptions
  if (noComponentPurchaseDueTo) noPlanChangeDueTo += noComponentPurchaseDueTo
  // 3- disabledUpdatePaymentDetails checks if user is not admin or does not have a subscription
  let changePlanSelfServe = (
    <RouteLink
      to="/settings/billing/plans"
      className={'SubscribedUserBilling_ChangePlanLink'}
    >
      Change Plan
    </RouteLink>
  )
  // Never falls back to the legacy plans page: a failed link stays disabled until the page reloads
  if (constance[CB_59_GROWTH_AT_MANAGE_SUBSCRIPTION]) {
    changePlanSelfServe = (
      <RouteLink
        disabled={!growthLink}
        to={growthLink ?? '/settings/billing'}
        target="_blank"
        rel="noopener noreferrer"
        className={clsx(
          'SubscribedUserBilling_ChangePlanLink',
          !growthLink && 'SubscribedUserBilling_ChangePlanLink--Disabled',
        )}
      >
        Change Plan (Chargee Growth demo)
      </RouteLink>
    )
  }
  const changePlanInterom = (
    <ContactUsLink
      type="button"
      className="SubscribedUserBilling_ContactUsLink"
      intercomMsg="I’d like to change plan"
    >
      Change Plan
    </ContactUsLink>
  )
  const changePlanNotAdmin = (
    <ContactUsLink
      type="button"
      disabled
      className={clsx(
        'SubscribedUserBilling_ContactUsLink',
        'SubscribedUserBilling_ContactUsButton--Disabled',
      )}
    >
      {WrapperUpdatePlanLink(`Change Plan`)}
    </ContactUsLink>
  )

  return (
    <div className="SubscribedUserBilling">
      <div className="SubscribedUserBilling_TitleWrapper">
        <Flex direction="column" gap="3">
          <Heading size={700}>Billing and Subscriptions</Heading>
          {showUpgradeCallout()}
        </Flex>
        {!showChangePlanButtonRollout && (
          <div>
            <Button
              caption="View pricing"
              appearance="primary"
              link="https://disco.ac/pricing"
              linkTarget="_blank"
            />
          </div>
        )}
      </div>
      <div className="SubscribedUserBilling_AlertContainer">
        {paymentOverdue && (
          <Alert status="danger" icon="warning">
            <p
              className={clsx('SubscribedUserBilling_AlertDescription', {
                'SubscribedUserBilling_AlertDescription--WithBottomButton':
                  updateBillingInfoButton,
              })}
            >
              Your account is overdue
            </p>

            {updateBillingInfoButton}
          </Alert>
        )}
      </div>
      {billingRenewsBanner}
      <Card title={`${planName} Plan`} billing planColour={planColour}>
        {switchTo && !bankDraftSubscriber && (
          <div className="SubscribedUserBilling_BillingInfo">
            {subscription.loading && (
              <span className="SubscribedUserBilling_BillingInfo_Amount">
                loading...
              </span>
            )}
            <Flex align="center" gap="1">
              {switchTo === 'annual' && (
                <Lozenge large color="blue" text="Save 10%" />
              )}
              <ContactUsLink
                className={clsx('SubscribedUserBilling_ContactUsLink', {
                  'SubscribedUserBilling_ContactUsLink--Disabled':
                    disabledUpdatePaymentDetails,
                })}
                intercomMsg={`I’d like to switch to ${switchTo} billing`}
                disabled={disabledUpdatePaymentDetails}
              >
                {WrapperUpdatePlanLink(`Switch to ${switchTo}`)}
              </ContactUsLink>
            </Flex>
            {subscription.loaded && (
              <>
                <span className="SubscribedUserBilling_BillingInfo_Amount">
                  <span className="SubscribedUserBilling_BillingInfo--Black">
                    {subscriptionAmount}
                  </span>
                  <span>
                    /<span>{switchTo === 'monthly' ? 'year' : 'month'}</span>
                  </span>
                </span>{' '}
                {subscriptionData?.scheduled_cancellation_at ? (
                  <span className="SubscribedUserBilling_BillingInfo_Renewal">
                    Expires on{' '}
                    <b>
                      {formatDate(subscriptionData.scheduled_cancellation_at)}
                    </b>
                  </span>
                ) : (
                  <span className="SubscribedUserBilling_BillingInfo_Renewal">
                    <span>
                      Renews{' '}
                      <span>
                        {switchTo === 'monthly' ? 'yearly' : 'monthly'}
                      </span>
                      , on{' '}
                    </span>
                    <strong>{billingRenewsDate}</strong>
                  </span>
                )}
              </>
            )}
          </div>
        )}
        {subscription.loaded && subscriptionData?.scheduled_cancellation_at && (
          <Callout.Root color="red" mt="3">
            <Callout.Text>
              Your subscription is cancelled and will{' '}
              <b>
                expire on{' '}
                {formatDate(subscriptionData?.scheduled_cancellation_at)}.
              </b>{' '}
              {!cancelSubscriptionModalOpen && (
                <>
                  Didn’t mean to cancel?{' '}
                  <button
                    className="SubscribedUserBilling_ContactUsLink inline-link"
                    data-testid="start-generic-intercom-workflow"
                    type="button"
                  >
                    Contact support
                  </button>
                </>
              )}
            </Callout.Text>
          </Callout.Root>
        )}
        <hr className="SubscribedUserBilling_Separator" />
        <div className="SubscribedUserBilling_Features">
          <span>
            {usersLimits.max === Infinity
              ? 'Unlimited users'
              : plural(usersLimits.max, 'user', 'users')}
          </span>
          <span>, {tracksCount} tracks</span>
        </div>
        <div className="SubscribedUserBilling_FeaturesList">
          {pricingPlanList}
        </div>

        {constance.SELF_SERVE_CANCELLATION_ROLLOUT &&
          constance['SUB-735-CHARGEBEE-CANCELLATION'] && (
            <Box mb="3">
              <ChargebeeScriptLoader
                onLoad={() => {
                  setHasChargebeeScriptLoaded(true)
                }}
                onError={err => {
                  setHasChargebeeScriptLoaded(false)
                  captureError(err, {
                    description:
                      'ChargebeeScriptLoader in SubscribedUserBillingCancelButton',
                  })
                }}
              />
            </Box>
          )}

        <Flex align="center" gap="4">
          <div>
            {showChangePlanButtonRollout && !noPlanChangeDueTo
              ? changePlanSelfServe
              : !showChangePlanButtonRollout && !disabledUpdatePaymentDetails
              ? changePlanInterom
              : changePlanNotAdmin}
            <StaffOnly
              explainValues={{
                'Self-Serve rollout': showChangePlanButtonRollout,
                'No Self-Serve due to': noPlanChangeDueTo.trim() || '-',
                'Button type: Self-Serve':
                  showChangePlanButtonRollout && !noPlanChangeDueTo,
                'Button type: Intercom':
                  !showChangePlanButtonRollout && !disabledUpdatePaymentDetails,
                'Button type: Not Admin': disabledUpdatePaymentDetails,
              }}
              controls={
                <>
                  <div>Button type: Self-Serve {changePlanSelfServe}</div>
                  <div>Button type: Intercom {changePlanInterom}</div>
                  <div>Button type: Not Admin {changePlanNotAdmin}</div>
                </>
              }
            />
          </div>
          {isAdmin && !subscriptionData?.scheduled_cancellation_at && (
            <SubscribedUserBillingCancelButton
              cancelSubscriptionModalOpen={cancelSubscriptionModalOpen}
              setCancelSubscriptionModalOpen={setCancelSubscriptionModalOpen}
              hasChargebeeScriptLoaded={hasChargebeeScriptLoaded}
            />
          )}
          <SubscriptionAdmin />
        </Flex>
        <ActionRow
          title="Inclusions"
          separator={true}
          leftContent={
            <>
              <List>
                <ListItemWithIcon
                  iconNode={<Icon type="profiles" color="#6B778C" />}
                >
                  <div
                    className={clsx('SubscribedUserBilling_ListItemWrapper', {
                      'SubscribedUserBilling_ListItemWrapper--Danger':
                        usersLimits.overQuota,
                    })}
                  >
                    <Link
                      to="/settings/users"
                      className="SubscribedUserBilling_ListItemLink"
                    >
                      Users
                    </Link>{' '}
                    <div>
                      <span className="SubscribedUserBilling_ListItemCount">
                        {usersLimits.current}
                      </span>{' '}
                      / {usersCount}
                      {usersLimits.updating && <span> (updating)</span>}
                    </div>
                  </div>
                </ListItemWithIcon>
              </List>
              {lastUserAddedDate ? (
                <div className="SubscribedUserBilling_InfoItem">
                  <Tooltip content={formatTimestamp(lastUserAddedDate)}>
                    Last user added {formatRelativeDate(lastUserAddedDate)}
                  </Tooltip>
                </div>
              ) : (
                <div>&nbsp;</div>
              )}
              {!subscription.error && usersLimits.max !== Infinity ? (
                <UpdateSeatsTracksLink
                  text="Add/remove seats"
                  intercomMsg={SEATS_MESSAGE}
                  enabled={enableChangeSeatsModal}
                />
              ) : (
                <div>&nbsp;</div>
              )}
            </>
          }
          rightContent={
            <>
              <List>
                <ListItemWithIcon
                  iconNode={<Icon type="lyrics" color="#6B778C" />}
                >
                  <div
                    className={clsx('SubscribedUserBilling_ListItemWrapper', {
                      'SubscribedUserBilling_ListItemWrapper--Danger':
                        tracksLimits.overQuota,
                    })}
                  >
                    Tracks{' '}
                    <div>
                      <span className="SubscribedUserBilling_ListItemCount">
                        {Number(tracksLimits.current).toLocaleString()}
                      </span>{' '}
                      / {<span>{tracksCount}</span>}
                      {tracksLimits.updating && <span> (updating)</span>}
                    </div>
                  </div>
                </ListItemWithIcon>
              </List>
              <div className="SubscribedUserBilling_InfoItem">
                <TracksMeter
                  current={tracksLimits.current}
                  max={tracksLimits.max}
                />
              </div>
              {!subscription.error && tracksLimits.max !== Infinity ? (
                <UpdateSeatsTracksLink
                  text="Add/remove tracks"
                  intercomMsg={TRACKS_MESSAGE}
                  enabled={enableChangeTracksModal}
                />
              ) : (
                <div>&nbsp;</div>
              )}
              <StaffOnly
                explainValues={{
                  'unlimited tracks': tracksLimits.max === Infinity,
                  'subscription loading': subscription.loading,
                  'subscription loaded': subscription.loaded,
                  'subscription error?': subscription.error || false,
                }}
              />
            </>
          }
        />
      </Card>

      {!subscription.error && (
        <>
          <Heading size={700}>Add Ons</Heading>

          <DiscoverySuiteAddOns />

          <WatermarkingAddOns />
        </>
      )}

      {/*
      <Card
       title="Watermarking"
       description="Watermark your playlists with our new watermarking feature."
       footerLinks={[
         {
           linkNode: (
             <ContactUsLink
               className="SubscribedUserBilling_ContactUsLink"
               intercomMsg="I’d like to subscribe to Watermarking"
             >
               Add to Plan
             </ContactUsLink>
           ),
         },
       ]}
      /> */}

      {showBillingDetailsTitle && <Heading size={700}>Billing Details</Heading>}
      {cardEnding && !bankDraftSubscriber && (
        <Card
          title="Credit Card information"
          footerLinks={[
            {
              linkNode: updateBillingInfoButton,
            },
            /* TODO: "View Statements" is not currently supported
          Asana task: https://app.asana.com/0/1178567259515450/1200448650170987/f
      {
        linkNode: (
          <ContactUsLink className="SubscribedUserBilling_ContactUsLink">
            View Statements
          </ContactUsLink>
        ),
      },*/
          ]}
        >
          <CardNumber />
        </Card>
      )}
      {!disabledUpdatePaymentDetails &&
        !constance['SUB_625_DISABLE_BILLING_ADDRESS_EDITING'] &&
        hasSubscription && (
          <Card title="Billing contact information">
            <BillingContactInformation showSections />
          </Card>
        )}
    </div>
  )
}

export default SubscribedUserBilling
